import { useQueryClient } from '@tanstack/react-query'
import { ArrowRight } from 'lucide-react'
import pluralize from 'pluralize'
import { useEffect, useState } from 'react'
import { toast } from 'sonner'

import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Field, FieldDescription, FieldGroup } from '@/components/ui/field'
import { Label } from '@/components/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Spinner } from '@/components/ui/spinner'
import { CacheKeys } from '../../../../lib/cache'
import { parseError } from '../../../../lib/errors'
import { bulkMoveHolders, type COIDocument, useBulkMovePreview } from '../../../../models/coi'
import { BulkMoveWarnings } from './BulkMoveWarnings'

/** "Auto Liability + Cargo" — the same label the COI documents tab lists. */
export function certificateLabel(document: COIDocument): string {
  const policies = document.policies ?? []
  if (!policies.length) return 'All policies'
  return policies.map((policy) => policy.vendorName || policy.label).join(' + ')
}

/**
 * Moves every holder of one certificate onto another.
 *
 * Nothing else in the product can do this — the holder serializer does not
 * expose `policies` — so a carrier that adds a policy has no way to put its
 * existing holders on the new combined certificate without this.
 */
export function BulkEditCertificateDialog({
  open,
  onOpenChange,
  documents,
  orgId,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  documents: COIDocument[]
  orgId?: string
}) {
  const [sourceKey, setSourceKey] = useState('')
  const [targetKey, setTargetKey] = useState('')
  const [isMoving, setIsMoving] = useState(false)
  const queryClient = useQueryClient()

  // Reset on each open: a half-filled move left over from last time is the one
  // thing nobody wants to submit by accident against a different certificate.
  useEffect(() => {
    if (open) {
      setSourceKey('')
      setTargetKey('')
    }
  }, [open])

  const selectable = documents.filter((document) => !!document.selectionKey)
  const target = selectable.find((document) => document.selectionKey === targetKey)
  const targetPolicyIds = (target?.policies ?? []).map((policy) => policy.id)
  const { preview, isLoading } = useBulkMovePreview(sourceKey, targetPolicyIds, orgId, open)

  const sameCertificate = !!sourceKey && sourceKey === targetKey
  const canMove = !!sourceKey && !!targetKey && !sameCertificate && !!preview && !preview.conflicts.length

  async function handleMove() {
    setIsMoving(true)
    try {
      const moved = await bulkMoveHolders(sourceKey, targetPolicyIds, orgId)
      queryClient.invalidateQueries({ queryKey: [CacheKeys.COI_CERTIFICATE_HOLDERS] })
      queryClient.invalidateQueries({ queryKey: [CacheKeys.COI_BULK_MOVE_PREVIEW] })
      onOpenChange(false)
      toast.success(
        `Moved ${pluralize('certificate holder', moved, true)}. Their certificates are being regenerated.`
      )
    } catch (error) {
      toast.error(parseError(error))
    } finally {
      setIsMoving(false)
    }
  }

  function picker(id: string, value: string, onValueChange: (next: string) => void, placeholder: string) {
    return (
      <Select value={value} onValueChange={onValueChange}>
        <SelectTrigger id={id} className="w-full">
          <SelectValue placeholder={placeholder} />
        </SelectTrigger>
        <SelectContent>
          {selectable.map((document) => (
            <SelectItem key={document.id} value={document.selectionKey as string}>
              {certificateLabel(document)} · v{document.version}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    )
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent size="lg" className="max-h-[85dvh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Change which certificate holders receive</DialogTitle>
          <DialogDescription>
            Moves every holder from one certificate onto another and regenerates their PDFs. Holders keep
            their name, address and email.
          </DialogDescription>
        </DialogHeader>

        <FieldGroup>
          <Field>
            <Label htmlFor="bulk-source">Move holders from</Label>
            {picker('bulk-source', sourceKey, setSourceKey, 'Select a certificate…')}
            <FieldDescription className="text-sm">
              Every holder currently on this certificate moves.
            </FieldDescription>
          </Field>

          <div className="text-muted-foreground flex justify-center">
            <ArrowRight className="size-4" />
          </div>

          <Field>
            <Label htmlFor="bulk-target">Onto</Label>
            {picker('bulk-target', targetKey, setTargetKey, 'Select a certificate…')}
          </Field>
        </FieldGroup>

        {sameCertificate && <p className="text-muted-foreground text-sm">Pick two different certificates.</p>}

        {isLoading && !!sourceKey && !!targetKey && (
          <div className="text-muted-foreground flex items-center gap-2 text-sm">
            <Spinner className="size-4" />
            Checking what would move…
          </div>
        )}

        {preview && !sameCertificate && <BulkMoveWarnings preview={preview} />}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={isMoving}>
            Cancel
          </Button>
          <Button
            onClick={handleMove}
            disabled={!canMove || isMoving}
            loading={isMoving ? 'Moving…' : undefined}
          >
            {preview && canMove ? `Move ${pluralize('holder', preview.movableCount, true)}` : 'Move holders'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
